import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageStyle } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import Color from "color";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { FC, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ProductMiniPhotoStyles;
    product: ProductModel;
    pdlDisplay?: boolean;
}

interface GalleryImagesModel {
    Id: string;
    SortOrder: number;
    Name: string;
    SmallImagePath: string;
    MediumImagePath: string;
    LargeImagePath: string;
    AltText: string;
}

type Props = OwnProps;

export interface ProductMiniPhotoStyles {
    wrapper?: InjectableCss;
    thumbsWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    miniPhotoLink?: LinkPresentationProps;
    thumbOverlay?: InjectableCss;
    thumbOverlayText?: TypographyPresentationProps;
    miniPhotoText?: TypographyPresentationProps;
    miniPhotoModal?: ModalPresentationProps;
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    arrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideWrapperStyles?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
}

const arrowWrapperStyles = {
    css: css`
        position: absolute;
        z-index: 1;
        top: 50%;
        margin-top: -20px;
        background-color: ${Color(COLORS.secondary.base).rgb().alpha(0.6).string()};
    `,
};

export const productMiniPhotoStyles: ProductMiniPhotoStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin: 10px 0 0;
        `,
    },
    thumbsWrapper: {
        css: css`
            display: flex;
        `,
    },
    imageWrapper: {
        css: css`
            overflow: hidden;
            position: relative;
            margin-right: 10px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["60px", null, "75px", null, null], properties: ["width"] },
                        { values: ["60px", null, "75px", null, null], properties: ["height"] },
                    ],
                    mediaOptions.min,
                )}

            ${LazyImageStyle} {
                width: 100%;
                height: 100%;
                border: 1px solid ${COLORS.border.darker};

                img {
                    width: auto;
                    height: 110%;
                    display: block;
                    position: absolute;
                    top: -5%;
                    left: 50%;
                    transform: translateX(-50%);
                }
            }
        `,
    },
    thumbOverlay: {
        css: css`
            position: absolute;
            width: 100%;
            height: 100%;
            background-color: ${Color(COLORS.secondary.base).rgb().alpha(0.8).string()};
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    thumbOverlayText: {
        color: COLORS.white,
        size: TYPOGRAPHY.size.large,
        weight: TYPOGRAPHY.weight.bold,
    },
    miniPhotoLink: {
        css: css`
            width: 100%;

            ${StyledTypography} > span {
                text-decoration: underline !important;
            }

            &:hover,
            &:focus {
                ${StyledTypography} > span {
                    text-decoration: none !important;
                }
            }
        `,
        typographyProps: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
    },
    miniPhotoText: {
        color: COLORS.secondary.dark,
        weight: TYPOGRAPHY.weight.bold,
    },
    miniPhotoModal: {
        cssOverrides: {
            modalBody: css`
                height: auto !important;
            `,
        },
    },
    slideshowWrapper: {
        css: css`
            padding: 10px 0;
            position: relative;
        `,
    },
    prevArrowWrapper: {
        ...arrowWrapperStyles,
    },
    arrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideWrapperStyles: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -10px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            min-height: 0;
            height: 3px;
            width: 30px;
            padding: 0;
            margin: 0 4px;
            border: 0;
            background: ${COLORS.border.base};
            &:hover,
            &:active {
                background: ${COLORS.border.base};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            ${arrowWrapperStyles?.css}
            right: 0;
        `,
    },
};

const ProductMiniPhoto = ({ product, extendedStyles, pdlDisplay }: Props) => {
    const [styles] = React.useState(() => mergeToNew(productMiniPhotoStyles, extendedStyles));
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [galleryImages, setGalleryImages] = useState<GalleryImagesModel[]>([]);
    const [emblaRef, embla] = useEmblaCarousel();

    const photoSlideshowHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        setSelectedIndex(0);
    };

    useEffect(() => {
        const getImages = product?.properties?.galleryImages ?? [];

        if (getImages.length) {
            const parsedData = JSON.parse(getImages) as GalleryImagesModel[];

            parsedData.sort((a, b) => {
                return a.SortOrder - b.SortOrder;
            });

            setGalleryImages(parsedData);
        }
    }, [product]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, galleryImages]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    if (!product || !galleryImages || !galleryImages.length) {
        return null;
    }

    const [thumbToDisplay, secondThumbToDisplay] = galleryImages;

    const renderForPdl = () => {
        return (
            <Link {...styles.miniPhotoLink} onClick={photoSlideshowHandler}>
                <StyledWrapper {...styles.thumbsWrapper}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <StyledWrapper {...styles.thumbOverlay}>
                            <Typography {...styles.thumbOverlayText}>+{galleryImages.length}</Typography>
                        </StyledWrapper>
                        <LazyImage src={thumbToDisplay.SmallImagePath} altText={thumbToDisplay.AltText} />
                    </StyledWrapper>
                </StyledWrapper>
            </Link>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {pdlDisplay ? (
                renderForPdl()
            ) : (
                <Link {...styles.miniPhotoLink} onClick={photoSlideshowHandler}>
                    <StyledWrapper {...styles.thumbsWrapper}>
                        <StyledWrapper {...styles.imageWrapper}>
                            <LazyImage src={thumbToDisplay.SmallImagePath} altText={thumbToDisplay.AltText} />
                        </StyledWrapper>
                        {secondThumbToDisplay && (
                            <StyledWrapper {...styles.imageWrapper}>
                                <StyledWrapper {...styles.thumbOverlay}>
                                    <Typography {...styles.thumbOverlayText}>+{galleryImages.length - 1}</Typography>
                                </StyledWrapper>
                                <LazyImage
                                    src={secondThumbToDisplay.SmallImagePath}
                                    altText={secondThumbToDisplay.AltText}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <Typography {...styles.miniPhotoText}>{translate("Preview on vehicles")}</Typography>
                </Link>
            )}
            <Modal
                headline={translate("View on vehicles")}
                {...styles.miniPhotoModal}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledWrapper {...styles.slideshowWrapper}>
                    {galleryImages.length > 1 && (
                        <StyledWrapper {...styles.prevArrowWrapper}>
                            <Button {...styles.arrowButton} onClick={() => embla && embla.scrollPrev()}>
                                <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.slideContainerWrapper}>
                            {galleryImages.map(({ LargeImagePath }) => {
                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <StyledWrapper key={LargeImagePath} {...styles.slideWrapperStyles}>
                                        <LazyImage src={LargeImagePath} altText={translate("View on vehicles")} />
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                    {galleryImages.length > 1 && (
                        <>
                            <StyledWrapper {...styles.dotsContainerWrapper}>
                                {galleryImages.map((_, i) => {
                                    const dotColorStyles =
                                        i === selectedIndex
                                            ? `
                                                background: ${COLORS.primary.base};
                                                &:hover { background: ${COLORS.primary.base}; }
                                            `
                                            : "";
                                    const dotButtonStyles = {
                                        css: css`
                                            ${styles.dotButton?.css || ""}
                                            ${dotColorStyles}
                                        `,
                                    };
                                    // eslint-disable-next-line react/no-array-index-key
                                    return (
                                        <Button
                                            key={i}
                                            {...dotButtonStyles}
                                            onClick={() => embla && embla.scrollTo(i)}
                                        />
                                    );
                                })}
                            </StyledWrapper>
                            <StyledWrapper {...styles.nextArrowWrapper}>
                                <Button {...styles.arrowButton} onClick={() => embla && scrollNext()}>
                                    <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                                </Button>
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

export default ProductMiniPhoto;
